import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import {
  isPasswordResetThrottledError,
  showThrottledResetToast,
  useAuth,
} from "@/core/auth";

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

export default function ForgotPassword() {
  const { forgotPassword, openResetCodeEntry } = useAuth();
  const navigate = useNavigate();

  return (
    <Stack
      direction="row"
      align="center"
      justify="center"
      fullWidth
      className="min-h-[calc(100vh-67px)] overflow-hidden p-6"
    >
      <Card className="w-full max-w-md p-6 pt-8">
        <Formik
          initialValues={{ email: "" }}
          validationSchema={forgotPasswordSchema}
          validateOnMount
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await forgotPassword(values);
            } catch (error) {
              if (isPasswordResetThrottledError(error)) {
                showThrottledResetToast(error.message, () =>
                  openResetCodeEntry(values.email),
                );
                return;
              }
              toast.error(
                error instanceof Error
                  ? error.message
                  : "Failed to send reset instructions, please try again",
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ getFieldProps, isSubmitting, isValid }) => (
            <Form>
              <Stack gap="lg" fullWidth>
                <Typography variant="subtitleW1" className="mt-4">
                  Forgot Password?
                </Typography>

                <Typography variant="bodyW1" textColor="secondary">
                  Enter your email address and we will send you reset
                  instructions.
                </Typography>

                <FormInput
                  id="email"
                  type="email"
                  label="Email"
                  placeholder="Enter your email"
                  {...getFieldProps("email")}
                  fullWidth
                />

                <Stack fullWidth gap="sm">
                  <Button
                    type="submit"
                    size="lg"
                    fullWidth
                    disabled={!isValid}
                    loading={isSubmitting}
                  >
                    Reset Password
                  </Button>

                  <Button
                    type="button"
                    size="lg"
                    variant="text"
                    fullWidth
                    className="[&_svg]:size-5"
                    leftIcon={<ArrowLeftIcon />}
                    onClick={() => navigate("/login")}
                  >
                    Back to sign in
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card>
    </Stack>
  );
}
